/**
 * Reusable "⋯" secondary-actions submenu for FK navigation entry points.
 *
 * Used by EP1 (FK popover), EP5 (related-section header), and as part of
 * EP2 / EP6 context menus. Surfaces two actions:
 *   - "Open in new explorer" — open a new TE tab on the target.
 *   - "Open in query editor" — open a new QE tab with `SELECT * …` SQL.
 *
 * The caller passes a `source` per the analytics glossary so each origin emits
 * the matching `feature:fk_nav_open_new_explorer` / `_open_query_editor` event.
 */

import { MoreHorizontal, ExternalLink, FileCode2, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { openInNewExplorer, openInQueryEditor } from '../services/openDrillTargetAction';
import type { SecondarySource } from '../services/openDrillTargetAction';
import type { DrillTarget } from '../utils/buildDrillSql';
import type { DatabaseEngineId } from '../../../../core/engines';

type DrillOpenInMenuProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly target: DrillTarget;
  readonly source: Extract<
    SecondarySource,
    'fk-popover-menu' | 'row-context-menu' | 'related-section-menu' | 'related-row-menu'
  >;
  /** Optional connection metadata for the new QE tab. */
  readonly connectionName?: string;
  readonly serverHost?: string;
  /** Optional callback fired after either secondary action runs. */
  readonly onPerformed?: () => void;
  readonly buttonTestId?: string;
  /**
   * Render the trigger as an "Open ⌄" labeled button instead of the kebab icon.
   * Used by the FK peek popover in QE context (no standalone "Open" button).
   */
  readonly variant?: 'kebab' | 'labeled';
};

export function DrillOpenInMenu({
  connectionId,
  database,
  engineId,
  target,
  source,
  connectionName,
  serverHost,
  onPerformed,
  buttonTestId,
  variant = 'kebab',
}: DrillOpenInMenuProps) {
  const { track } = useAnalytics();

  const handleOpenInNewExplorer = () => {
    openInNewExplorer({
      connectionId,
      database,
      engineId,
      target,
    });
    track('feature:fk_nav_open_new_explorer', {
      source,
      targetKind: target.kind,
      engine: engineId,
    });
    onPerformed?.();
  };

  const handleOpenInQueryEditor = () => {
    openInQueryEditor({
      connectionId,
      database,
      engineId,
      target,
      connectionName,
      serverHost,
    });
    track('feature:fk_nav_open_query_editor', {
      source,
      targetKind: target.kind,
      engine: engineId,
    });
    onPerformed?.();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'labeled' ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="h-7 px-2 text-xs"
            title="Open options"
            aria-label="Open options"
            data-testid={buttonTestId ?? 'drill-open-in-menu-trigger'}
          >
            Open
            <ChevronDown className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="More open options"
            aria-label="More open options"
            data-testid={buttonTestId ?? 'drill-open-in-menu-trigger'}
          >
            <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="text-xs">
        <DropdownMenuItem
          onClick={handleOpenInNewExplorer}
          data-testid="drill-open-in-new-explorer"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Open in new explorer
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleOpenInQueryEditor}
          data-testid="drill-open-in-query-editor"
        >
          <FileCode2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Open in query editor
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
